import BigNumber from "bignumber.js";
import {
  truncateDecimals,
  formatWithSignificantDigits,
  formatWithThousandSpaces,
} from "./numberFormatter";

interface CurrencyFormatterOptions {
  locale?: string;
  decimalPlaces?: number;
  cryptocurrencyDecimalPlaces?: number;
  stablecoins?: string[];
  useDynamicDecimals?: boolean;
}

interface FormatOptions {
  useDynamicDecimals?: boolean;
  showSymbol?: boolean;
}

class CurrencyFormatter {
  locale: string;
  decimalPlaces: number;
  cryptocurrencyDecimalPlaces: number;
  stablecoins: string[];
  fiat: string[];
  twoDecimalCurrencies: string[];
  useDynamicDecimals: boolean;

  constructor(options: CurrencyFormatterOptions = {}) {
    this.locale = options.locale || "en-ZA";
    this.decimalPlaces = options.decimalPlaces || 2;
    this.cryptocurrencyDecimalPlaces = options.cryptocurrencyDecimalPlaces || 2;
    this.stablecoins = options.stablecoins || ["USDT", "USDC", "DAI"];
    this.fiat = ["ZAR", "USD"];
    this.twoDecimalCurrencies = ["USD", "ZAR", "USDT", "USDC"];
    this.useDynamicDecimals = options.useDynamicDecimals || false;
  }

  private normalizeFiatAmount(amount: number | string): number {
    if (typeof amount === "number") {
      return Number.isFinite(amount) ? amount : 0;
    }

    const trimmed = amount.trim();
    if (!trimmed) {
      return 0;
    }

    const commaDecimalMatch = trimmed.match(/^(-?[\d\s]+),(\d{1,2})$/);
    const normalized = commaDecimalMatch
      ? `${commaDecimalMatch[1].replace(/\s/g, "")}.${commaDecimalMatch[2]}`
      : trimmed.replace(/[^\d.-]/g, "");

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private formatTruncatedFiat(value: number, decimals: number): string {
    return formatWithThousandSpaces(value, decimals);
  }

  formatFiat(amount: number | string, currency: string): string {
    const currencyUpper = currency?.toUpperCase();
    const numAmount = this.normalizeFiatAmount(amount);
    const bigAmount = new BigNumber(numAmount);
    const currencySymbol =
      currencyUpper === "ZAR"
        ? "R"
        : currencyUpper === "USD"
          ? "$"
          : currencyUpper;
    const mustUseTwoDecimals =
      this.twoDecimalCurrencies.includes(currencyUpper);
    const fiatDecimalPlaces = 2;

    if (this.useDynamicDecimals && !mustUseTwoDecimals) {
      let decimalPlaces: number;
      if (bigAmount.isZero()) {
        decimalPlaces = fiatDecimalPlaces;
      } else {
        const integerPart = bigAmount.abs().integerValue();
        const integerDigits = integerPart.toString().length;
        decimalPlaces = Math.max(fiatDecimalPlaces, 8 - integerDigits);
      }

      return `${currencySymbol} ${this.formatTruncatedFiat(
        bigAmount.toNumber(),
        decimalPlaces
      )}`;
    }

    return `${currencySymbol} ${this.formatTruncatedFiat(
      bigAmount.toNumber(),
      fiatDecimalPlaces
    )}`;
  }

  formatCrypto(
    amount: number | string,
    symbol: string,
    showSymbol: boolean = true
  ): string {
    const bigAmount = new BigNumber(amount);
    const symbolUpper = symbol?.toUpperCase();
    const numValue = bigAmount.toNumber();

    // Stablecoins and currencies that require 2 decimal places
    const mustUseTwoDecimals = this.twoDecimalCurrencies.includes(symbolUpper);
    if (this.stablecoins.includes(symbolUpper) || mustUseTwoDecimals) {
      const truncated = truncateDecimals(numValue, 2);
      return showSymbol ? `${truncated} ${symbol}` : truncated;
    }

    // For all other crypto: use smart formatting with significant digits
    const maxDecimals =
      symbolUpper === "BTC" || symbolUpper === "BITCOIN" ? 8 : 6;
    const formatted = formatWithSignificantDigits(numValue, {
      minSignificant: 4,
      maxDecimals: maxDecimals,
    });

    return showSymbol ? `${formatted} ${symbol}` : formatted;
  }

  format(
    amount: number | string,
    typeOrCurrency: string = "USD",
    currencyOrOptions?: string | FormatOptions,
    options?: FormatOptions
  ): string {
    const originalUseDynamicDecimals = this.useDynamicDecimals;

    // Support both signatures:
    // format(amount, currencyOrSymbol, options?)        — auto-detect fiat/crypto
    // format(amount, "fiat"|"crypto", symbol, options?) — explicit type
    let type: "fiat" | "crypto";
    let symbol: string;
    let formatOptions: FormatOptions;

    if (typeOrCurrency === "fiat" || typeOrCurrency === "crypto") {
      // Explicit type signature: format(amount, type, symbol, options?)
      type = typeOrCurrency;
      symbol = (currencyOrOptions as string) || "USD";
      formatOptions = options || {};
    } else {
      // Auto-detect signature: format(amount, currencyOrSymbol, options?)
      symbol = typeOrCurrency;
      formatOptions = (currencyOrOptions as FormatOptions) || {};
      const currencyUpper = symbol?.toUpperCase();
      type =
        this.fiat.includes(symbol) || currencyUpper === "USD"
          ? "fiat"
          : "crypto";
    }

    if (formatOptions.useDynamicDecimals !== undefined) {
      this.useDynamicDecimals = formatOptions.useDynamicDecimals;
    }

    const result =
      type === "crypto"
        ? this.formatCrypto(
            amount,
            symbol,
            formatOptions.showSymbol ?? type === "crypto"
          )
        : this.formatFiat(amount, symbol);

    this.useDynamicDecimals = originalUseDynamicDecimals;

    return result;
  }
}

export default CurrencyFormatter;
